/**
 * AgentBroadcast.js — Client Broadcast Center
 *
 * Allows the agent to send a single announcement to ALL their leads at once.
 * Each lead receives:
 *   • A notification card in their 🔔 Updates tab (lead portal)
 *   • An automatic SMS via GHL
 *   • An automatic Email via GHL
 *
 * The lead list is fully selectable:
 *   - All leads selected by default
 *   - Agent can uncheck individual leads or use "Select All / Deselect All"
 *   - Only checked leads receive the broadcast
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';

const INS_OPTIONS = [
  { value: '',         label: '🌐 All Insurance Types' },
  { value: 'aca',      label: '🏥 ACA Health' },
  { value: 'medicare', label: '👴 Medicare' },
  { value: 'dental',   label: '🦷 Dental' },
  { value: 'vision',   label: '👁️ Vision' },
  { value: 'life',     label: '❤️ Life' },
];

const INS_LABELS = { aca: 'ACA', medicare: 'Medicare', dental: 'Dental', vision: 'Vision', life: 'Life' };
const INS_COLORS = { aca: '#1a56db', medicare: '#7c3aed', dental: '#0891b2', vision: '#059669', life: '#dc2626' };

function authHeaders() {
  return { headers: { Authorization: `Bearer ${localStorage.getItem('agentToken')}` } };
}

const QUICK_TEMPLATES = [
  {
    label: '📋 Application Submitted',
    title: 'Your application has been submitted ✅',
    body:  'Great news! Your insurance application has been successfully submitted. You will receive a confirmation from the carrier within 3–5 business days. Feel free to reach out if you have any questions.'
  },
  {
    label: '🎉 Coverage Approved',
    title: 'Your coverage has been approved 🎉',
    body:  'Congratulations! Your insurance coverage has been approved by the carrier. Your effective date and plan details are now available in your Coverage tab. Please review them and let us know if anything needs to be updated.'
  },
  {
    label: '📄 Document Required',
    title: 'Action Required — Document Needed 📎',
    body:  'We need a document from you to continue processing your application. Please log in to your portal and upload the required document in the Upload Docs section. This is time-sensitive — please respond within 48 hours.'
  },
  {
    label: '🔄 Open Enrollment Reminder',
    title: 'Open Enrollment is Here — Review Your Plan 🔄',
    body:  'Open enrollment season is now open. This is your chance to review your current coverage, explore better options, or add new benefits. Contact us to schedule a free review — it only takes 15 minutes.'
  },
  {
    label: '📞 Check-In',
    title: 'Checking in — How can we help? 👋',
    body:  "Hi! We're reaching out to check in and make sure you're happy with your current coverage. If you have any questions, need help using your benefits, or want to explore any changes, we're here for you. Just reply or give us a call."
  },
];

export default function AgentBroadcast() {
  const [leads, setLeads]               = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(true);
  const [insFilter, setInsFilter]       = useState('');
  const [search, setSearch]             = useState('');
  const [title, setTitle]               = useState('');
  const [body, setBody]                 = useState('');
  const [sending, setSending]           = useState(false);
  const [result, setResult]             = useState(null);
  const [error, setError]               = useState('');
  const [confirmOpen, setConfirmOpen]   = useState(false);
  const [preview, setPreview]           = useState(false);

  // ── Per-lead selection (Set of leadEmail strings) ─────────────────────────
  // All leads selected by default; syncs when filter or leads list changes.
  const [selectedEmails, setSelectedEmails] = useState(new Set());

  // Fetch leads on mount
  useEffect(() => {
    axios.get('/api/lead-portal/agent/leads', authHeaders())
      .then(r => {
        const list = r.data.leads || [];
        setLeads(list);
        setSelectedEmails(new Set(list.map(l => l.leadEmail)));
      })
      .catch(console.error)
      .finally(() => setLeadsLoading(false));
  }, []);

  // Leads matching the insurance-type filter + search
  const targetLeads = leads
    .filter(l => !insFilter || l.insurances.some(i => i.insuranceType === insFilter))
    .filter(l => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (l.name || '').toLowerCase().includes(q)
          || (l.leadEmail || '').toLowerCase().includes(q)
          || (l.phone || '').toLowerCase().includes(q);
    });

  // When filter changes → auto-select all leads in the new filtered set
  useEffect(() => {
    setSelectedEmails(new Set(targetLeads.map(l => l.leadEmail)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insFilter, leads.length]);

  // Leads that are both in the filter AND checked
  const recipientLeads = targetLeads.filter(l => selectedEmails.has(l.leadEmail));

  const allSelected  = targetLeads.length > 0 && targetLeads.every(l => selectedEmails.has(l.leadEmail));
  const noneSelected = targetLeads.length > 0 && targetLeads.every(l => !selectedEmails.has(l.leadEmail));

  const toggleLead = (email) => {
    setSelectedEmails(prev => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email); else next.add(email);
      return next;
    });
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelectedEmails(prev => {
        const next = new Set(prev);
        targetLeads.forEach(l => next.delete(l.leadEmail));
        return next;
      });
    } else {
      setSelectedEmails(prev => {
        const next = new Set(prev);
        targetLeads.forEach(l => next.add(l.leadEmail));
        return next;
      });
    }
  };

  const canSend = title.trim() && body.trim() && recipientLeads.length > 0 && !sending;

  const applyTemplate = (tpl) => {
    setTitle(tpl.title);
    setBody(tpl.body);
    setResult(null);
    setError('');
  };

  const handleSend = async () => {
    if (!canSend) return;
    setConfirmOpen(false);
    setSending(true);
    setResult(null);
    setError('');
    try {
      const { data } = await axios.post(
        '/api/lead-portal/agent/broadcast',
        {
          title:         title.trim(),
          body:          body.trim(),
          insuranceType: insFilter || undefined,
          // Explicit list of checked lead emails — server sends only to these
          leadEmails:    [...selectedEmails],
        },
        authHeaders()
      );
      setResult(data);
      setTitle('');
      setBody('');
      setInsFilter('');
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Broadcast failed. Please try again.');
    } finally {
      setSending(false);
    }
  };

  /* ── Stat cards ── */
  const statGroups = {};
  for (const lead of leads) {
    for (const ins of lead.insurances) {
      const t = ins.insuranceType || 'other';
      statGroups[t] = (statGroups[t] || 0) + 1;
    }
  }
  const uniqueLeads = new Set(leads.map(l => l.leadEmail)).size;

  return (
    <div style={{ padding: '24px', maxWidth: 960, margin: '0 auto' }}>
      {/* ── Page header ── */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '6px' }}>
          <span style={{ fontSize: '28px' }}>📢</span>
          <div>
            <h1 style={{ margin: 0, fontSize: '22px', fontWeight: 800, color: '#111827' }}>Client Broadcast Center</h1>
            <p style={{ margin: 0, fontSize: '14px', color: '#6b7280', marginTop: '3px' }}>
              Send an announcement to your leads — select who receives it, then hit Send.
            </p>
          </div>
        </div>
      </div>

      {/* ── Stat cards ── */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '14px 18px', minWidth: 120 }}>
          <div style={{ fontSize: '24px', fontWeight: 800, color: '#111827' }}>{leadsLoading ? '…' : uniqueLeads}</div>
          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>Total Leads</div>
        </div>
        {Object.entries(statGroups).map(([ins, cnt]) => (
          <div key={ins} style={{ background: '#fff', border: `1px solid ${(INS_COLORS[ins] || '#e5e7eb')}40`, borderRadius: '10px', padding: '14px 18px', minWidth: 100 }}>
            <div style={{ fontSize: '24px', fontWeight: 800, color: INS_COLORS[ins] || '#374151' }}>{cnt}</div>
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{INS_LABELS[ins] || ins.toUpperCase()}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: '20px', alignItems: 'start' }}>

        {/* ── Left: Compose ── */}
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '24px' }}>
          <h2 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: 700, color: '#111827' }}>Compose Broadcast</h2>

          {/* Quick templates */}
          <div style={{ marginBottom: '18px' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Quick Templates</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {QUICK_TEMPLATES.map(tpl => (
                <button key={tpl.label} onClick={() => applyTemplate(tpl)}
                  style={{ background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '5px 10px', fontSize: '12px', cursor: 'pointer', color: '#374151', whiteSpace: 'nowrap' }}>
                  {tpl.label}
                </button>
              ))}
            </div>
          </div>

          {/* Insurance filter */}
          <div style={{ marginBottom: '14px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
              Filter By Insurance Type
            </label>
            <select value={insFilter} onChange={e => setInsFilter(e.target.value)}
              style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: '8px', padding: '10px 12px', fontSize: '14px', background: '#fff', color: '#111827' }}>
              {INS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '5px' }}>
              {leadsLoading ? 'Loading…' : `Showing ${targetLeads.length} lead${targetLeads.length !== 1 ? 's' : ''} — select individual recipients on the right.`}
            </p>
          </div>

          {/* Title */}
          <div style={{ marginBottom: '14px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>Title *</label>
            <input
              value={title}
              onChange={e => { setTitle(e.target.value); setResult(null); setError(''); }}
              placeholder="e.g. 'Open Enrollment — Review Your Plan Now'"
              style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: '8px', padding: '10px 12px', fontSize: '14px', boxSizing: 'border-box', fontWeight: 600 }}
            />
          </div>

          {/* Body */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
              Message Body *
            </label>
            <textarea
              value={body}
              onChange={e => { setBody(e.target.value); setResult(null); setError(''); }}
              placeholder="Write your announcement here. Be clear and concise. This will be sent as SMS and Email as well."
              rows={6}
              style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: '8px', padding: '10px 12px', fontSize: '14px', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 }}
            />
            <div style={{ fontSize: '11px', color: '#9ca3af', textAlign: 'right', marginTop: '3px' }}>{body.length} characters</div>
          </div>

          {/* Error */}
          {error && (
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', padding: '12px 14px', fontSize: '13px', color: '#dc2626', marginBottom: '16px' }}>
              ⚠️ {error}
            </div>
          )}

          {/* Success */}
          {result && (
            <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px', padding: '14px', fontSize: '13px', color: '#15803d', marginBottom: '16px' }}>
              <div style={{ fontWeight: 700, fontSize: '14px', marginBottom: '4px' }}>🎉 Broadcast Sent!</div>
              <div>✅ Delivered to <strong>{result.sentCount} lead{result.sentCount !== 1 ? 's' : ''}</strong> — each received an in-app update, SMS, and Email.</div>
              {result.failedCount > 0 && <div style={{ marginTop: '4px', color: '#d97706' }}>⚠️ {result.failedCount} failed to save.</div>}
            </div>
          )}

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => setPreview(v => !v)}
              disabled={!title.trim() && !body.trim()}
              style={{ flex: 1, background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: '8px', padding: '11px 16px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>
              {preview ? 'Hide Preview' : '👁 Preview'}
            </button>
            <button
              onClick={() => canSend && setConfirmOpen(true)}
              disabled={!canSend}
              style={{ flex: 2, background: canSend ? '#1a56db' : '#9ca3af', color: '#fff', border: 'none', borderRadius: '8px', padding: '11px 16px', fontSize: '14px', fontWeight: 700, cursor: canSend ? 'pointer' : 'default' }}>
              {sending
                ? '📡 Sending…'
                : `📢 Send to ${recipientLeads.length} Lead${recipientLeads.length !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>

        {/* ── Right: Preview + Selectable lead list ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

          {/* Preview card (what lead sees) */}
          {preview && (
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '18px' }}>
              <div style={{ fontSize: '12px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>Lead Portal Preview</div>
              <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                  <span style={{ fontSize: '14px', fontWeight: 700, color: '#111827', flex: 1 }}>{title || 'Update Title'}</span>
                  <span style={{ background: '#eff6ff', color: '#1a56db', borderRadius: '999px', padding: '2px 8px', fontSize: '11px', fontWeight: 700, marginLeft: 8, flexShrink: 0 }}>New</span>
                </div>
                <p style={{ fontSize: '13px', color: '#374151', lineHeight: 1.6, margin: '0 0 8px' }}>{body || 'Message body will appear here…'}</p>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '11px', color: '#9ca3af' }}>Your Agent</span>
                  <span style={{ fontSize: '11px', color: '#9ca3af' }}>Just now</span>
                </div>
              </div>
              <p style={{ fontSize: '11px', color: '#9ca3af', textAlign: 'center', marginTop: '8px' }}>
                + Automatic SMS and Email will also be sent
              </p>
            </div>
          )}

          {/* Selectable lead list */}
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '16px' }}>
            {/* Search input */}
            <div style={{ marginBottom: '10px' }}>
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by name, email or phone…"
                style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
            {/* Header row */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <div style={{ fontSize: '13px', fontWeight: 700 }}>
                <span style={{ color: recipientLeads.length > 0 ? '#1a56db' : '#9ca3af', fontSize: '16px' }}>{recipientLeads.length}</span>
                <span style={{ color: '#6b7280' }}> / {targetLeads.length} selected</span>
                {search.trim() && <span style={{ color: '#9ca3af', fontWeight: 400, fontSize: '12px' }}> (filtered)</span>}
              </div>
              {targetLeads.length > 0 && (
                <button
                  onClick={toggleAll}
                  style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: '6px', padding: '4px 10px', fontSize: '11px', cursor: 'pointer', color: '#374151', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                  {allSelected
                    ? <><span style={{ color: '#1a56db' }}>☑</span> Deselect All</>
                    : <><span>☐</span> Select All</>}
                </button>
              )}
            </div>

            {leadsLoading ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#9ca3af', fontSize: '13px' }}>Loading…</div>
            ) : targetLeads.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#9ca3af', fontSize: '13px' }}>
                {search.trim() ? `No leads match "${search}".` : 'No leads match the selected filter.'}
              </div>
            ) : (
              <div style={{ maxHeight: '340px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                {targetLeads.map(l => {
                  const checked = selectedEmails.has(l.leadEmail);
                  return (
                    <div
                      key={l.leadEmail}
                      onClick={() => toggleLead(l.leadEmail)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '10px',
                        padding: '9px 10px', borderRadius: '8px', cursor: 'pointer',
                        background: checked ? '#eff6ff' : '#f9fafb',
                        border: `1px solid ${checked ? '#bfdbfe' : '#e5e7eb'}`,
                        transition: 'all .12s',
                        userSelect: 'none',
                      }}>
                      {/* Custom checkbox */}
                      <div style={{
                        width: 18, height: 18, borderRadius: '4px', flexShrink: 0,
                        border: `2px solid ${checked ? '#1a56db' : '#d1d5db'}`,
                        background: checked ? '#1a56db' : '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'all .12s',
                      }}>
                        {checked && <span style={{ color: '#fff', fontSize: '11px', lineHeight: 1, fontWeight: 700 }}>✓</span>}
                      </div>
                      {/* Avatar */}
                      <div style={{
                        width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                        background: checked ? '#1a56db' : '#9ca3af',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff', fontWeight: 700, fontSize: '12px',
                        transition: 'background .12s',
                      }}>
                        {(l.leadName || l.leadEmail || '?')[0].toUpperCase()}
                      </div>
                      {/* Info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: checked ? '#1e40af' : '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {l.leadName || l.leadEmail}
                        </div>
                        <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                          {l.insurances
                            .filter(i => !insFilter || i.insuranceType === insFilter)
                            .map(i => INS_LABELS[i.insuranceType] || i.insuranceType)
                            .join(', ')}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* "None selected" warning */}
            {!leadsLoading && noneSelected && (
              <div style={{ marginTop: '10px', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: '8px', padding: '8px 12px', fontSize: '12px', color: '#92400e' }}>
                ⚠️ No leads selected — select at least one to send.
              </div>
            )}
          </div>

        </div>
      </div>

      {/* ── Confirm modal ── */}
      {confirmOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '28px', maxWidth: '440px', width: '100%', boxShadow: '0 20px 48px rgba(0,0,0,.2)' }}>
            <div style={{ fontSize: '40px', textAlign: 'center', marginBottom: '14px' }}>📢</div>
            <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: 700, textAlign: 'center', color: '#111827' }}>Confirm Broadcast</h3>
            <p style={{ fontSize: '14px', color: '#6b7280', textAlign: 'center', lineHeight: 1.6, margin: '0 0 6px' }}>
              You're about to send <strong>"{title}"</strong> to
            </p>
            <p style={{ fontSize: '20px', fontWeight: 800, textAlign: 'center', color: '#1a56db', margin: '0 0 6px' }}>
              {recipientLeads.length} lead{recipientLeads.length !== 1 ? 's' : ''}
            </p>
            <p style={{ fontSize: '13px', color: '#9ca3af', textAlign: 'center', margin: '0 0 22px' }}>
              Each lead will receive an in-app notification, SMS, and Email.
            </p>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setConfirmOpen(false)}
                style={{ flex: 1, background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: '8px', padding: '11px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={handleSend}
                style={{ flex: 2, background: '#1a56db', color: '#fff', border: 'none', borderRadius: '8px', padding: '11px', fontSize: '14px', fontWeight: 700, cursor: 'pointer' }}>
                ✈️ Yes, Send Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
